/**
 * Hero slideshow.
 *
 * Loaded only by a hero that actually has extra slides, so a single-image hero
 * costs nothing. Like every other component here it upgrades markup that is
 * already correct: with the script off, the first slide shows and the controls
 * stay `hidden`.
 *
 * Accessibility, in the order it matters:
 *   - prefers-reduced-motion switches autoplay off entirely. WCAG 2.3.3 is
 *     about transitions, but a hero that rotates on its own is movement the
 *     customer did not ask for, so the honest reading is: don't.
 *   - a pause control, because WCAG 2.2.2 wants a mechanism for anything that
 *     moves for more than five seconds, and dots are navigation, not a stop.
 *   - aria-live is `off` while it rotates by itself and `polite` once the
 *     customer has taken control, which is the APG carousel pattern: announcing
 *     every automatic change would talk over the page.
 *   - autoplay also stops on hover, on focus inside, and while the tab is
 *     hidden.
 */
(function () {
  'use strict';

  if (!window.Theme) return;

  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

  class HeroSlideshow extends window.Theme.ThemeElement {
    init() {
      this.slidesContainer = this.querySelector('[data-hero-slides]');
      this.slides = this.$$('[data-hero-slide]');
      if (!this.slidesContainer || this.slides.length < 2) return;

      this.dots = this.$$('[data-hero-dot]');
      this.controls = this.querySelector('[data-hero-controls]');
      this.toggle = this.querySelector('[data-hero-toggle]');
      this.iconPause = this.querySelector('[data-hero-icon-pause]');
      this.iconPlay = this.querySelector('[data-hero-icon-play]');

      this.interval = Number(this.dataset.interval) || 5000;
      this.reduced = window.matchMedia(REDUCED_MOTION);
      this.wantsAutoplay = this.dataset.autoplay === 'true' && !this.reduced.matches;
      this.playing = false;
      this.timer = null;
      this.index = 0;

      this.labelSlides();

      // The `data-ready` flag hands the "which slide shows" decision from CSS
      // (:first-child) to the script (.is-current). Set it before the first
      // setCurrent so there is never a frame with no visible slide.
      this.dataset.ready = 'true';
      this.setCurrent(0, { announce: false });

      if (this.controls) this.controls.hidden = false;
      this.wireControls();
      this.wireSwipe();
      this.wirePauseTriggers();

      if (this.wantsAutoplay) {
        this.play();
      } else {
        // Nothing rotates by itself, so every change from here is one the
        // customer asked for and is worth announcing.
        this.setLive('polite');
        this.reflectToggle();
      }
    }

    destroy() {
      this.stopTimer();
    }

    /* --------------------------------------------------------------- setup */

    /**
     * Slide labels are set here rather than in Liquid because the lead slide is
     * a section setting and the rest are theme blocks — neither knows the total,
     * and a block cannot know its own position. The DOM does.
     */
    labelSlides() {
      const template = this.dataset.slideLabel || '';
      const roleDescription = this.dataset.slideRoledescription || '';
      this.slides.forEach((slide, i) => {
        slide.setAttribute('role', 'group');
        if (roleDescription) slide.setAttribute('aria-roledescription', roleDescription);
        if (template) slide.setAttribute('aria-label', template.replace('[index]', i + 1));
      });
    }

    wireControls() {
      const prev = this.querySelector('[data-hero-prev]');
      const next = this.querySelector('[data-hero-next]');

      if (prev) this.on(prev, 'click', () => this.step(-1));
      if (next) this.on(next, 'click', () => this.step(1));

      this.dots.forEach((dot) => {
        this.on(dot, 'click', () => this.goTo(Number(dot.dataset.index)));
      });

      if (this.toggle) {
        this.on(this.toggle, 'click', () => {
          if (this.playing) this.pause({ sticky: true });
          else this.play();
        });
      }

      // Arrow keys move the slideshow only when the focus is already inside it,
      // so they never steal the page's own scrolling.
      this.on(this, 'keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        }
      });
    }

    /** Horizontal swipe, from pointer events — no library, no scroll hijacking. */
    wireSwipe() {
      let startX = null;
      let startY = null;

      this.on(this.slidesContainer, 'pointerdown', (event) => {
        if (event.pointerType === 'mouse') return;
        startX = event.clientX;
        startY = event.clientY;
      });

      this.on(this.slidesContainer, 'pointerup', (event) => {
        if (startX === null) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        startX = null;
        startY = null;
        // Ignore anything that is mostly vertical: that is the page scrolling.
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        this.step(dx < 0 ? 1 : -1);
      });

      this.on(this.slidesContainer, 'pointercancel', () => {
        startX = null;
        startY = null;
      });
    }

    wirePauseTriggers() {
      this.on(this, 'pointerenter', () => this.pause());
      this.on(this, 'pointerleave', () => this.resume());
      this.on(this, 'focusin', () => this.pause());
      this.on(this, 'focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.resume();
      });

      this.on(document, 'visibilitychange', () => {
        if (document.hidden) this.pause();
        else this.resume();
      });

      // Reduced motion can be switched either way mid-session, and both
      // directions have to be honoured: turning it on stops the rotation, and
      // turning it off has to give the play button back — otherwise the
      // slideshow stays frozen with no visible way to start it.
      if (typeof this.reduced.addEventListener === 'function') {
        this.on(this.reduced, 'change', (event) => {
          this.wantsAutoplay = this.dataset.autoplay === 'true' && !event.matches;
          if (event.matches) this.pause({ sticky: true });
          // Deliberately not auto-restarting: offer the control, don't take the
          // decision. A customer who had pressed pause still means it.
          else this.reflectToggle();
        });
      }
    }

    /* ------------------------------------------------------------ playback */

    play() {
      if (this.reduced.matches) return;
      this.stopped = false;
      this.playing = true;
      this.startTimer();
      this.setLive('off');
      this.reflectToggle();
    }

    /**
     * `sticky` is the customer saying stop — it survives the pointer leaving.
     * A plain pause is hover, focus or a hidden tab, and resume undoes it.
     */
    pause(options) {
      if (options && options.sticky) this.stopped = true;
      this.playing = false;
      this.stopTimer();
      this.setLive('polite');
      this.reflectToggle();
    }

    resume() {
      if (this.stopped || !this.wantsAutoplay || document.hidden) return;
      this.play();
    }

    startTimer() {
      this.stopTimer();
      this.timer = window.setInterval(() => this.step(1, { announce: false }), this.interval);
    }

    stopTimer() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    reflectToggle() {
      if (!this.toggle) return;
      // With autoplay off in the settings, or reduced motion on, there is
      // nothing to pause and the button would be a lie.
      this.toggle.hidden = !this.wantsAutoplay;
      const label = this.playing
        ? this.toggle.dataset.labelPause
        : this.toggle.dataset.labelPlay;
      this.toggle.setAttribute('aria-label', label || '');
      if (this.iconPause) this.iconPause.hidden = !this.playing;
      if (this.iconPlay) this.iconPlay.hidden = this.playing;
    }

    setLive(value) {
      if (this.slidesContainer) this.slidesContainer.setAttribute('aria-live', value);
    }

    /* ----------------------------------------------------------- movement */

    step(delta, options) {
      const count = this.slides.length;
      this.goTo((this.index + delta + count) % count, options);
    }

    /** A customer-driven move also stops the automatic one, as APG asks. */
    goTo(index, options) {
      const settings = options || {};
      if (settings.announce !== false && this.playing) this.pause({ sticky: true });
      this.setCurrent(index, settings);
    }

    setCurrent(index, options) {
      if (index < 0 || index >= this.slides.length) return;
      this.index = index;

      this.slides.forEach((slide, i) => {
        const current = i === index;
        slide.classList.toggle('is-current', current);
        // Hidden slides are out of the accessibility tree, so a screen reader
        // never reads four photos as if they were all on the page.
        slide.setAttribute('aria-hidden', current ? 'false' : 'true');
      });

      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      const settings = options || {};
      if (settings.announce !== false && !this.playing) {
        const slide = this.slides[index];
        const label = slide && slide.getAttribute('aria-label');
        if (label) window.Theme.announce(label);
      }
    }
  }

  if (!customElements.get('hero-slideshow')) {
    customElements.define('hero-slideshow', HeroSlideshow);
  }
})();
